import React, { useState, useEffect } from 'react';
import {
  ClockIcon,
  UserGroupIcon,
  SpeakerWaveIcon,
  HeartIcon,
  BeakerIcon,
  CameraIcon,
  BuildingStorefrontIcon,
  ArrowPathIcon,
  ExclamationTriangleIcon,
  UserIcon,
  CubeIcon,
  ComputerDesktopIcon,
  MagnifyingGlassIcon,
  ArrowsPointingOutIcon,
  ArrowsPointingInIcon,
  Cog6ToothIcon,
  EyeIcon,
  EyeSlashIcon,
  XMarkIcon
} from '@heroicons/react/24/outline';
import { queueService, QueueDisplay as QueueDisplayType } from '../../services/queueService';
import { clinicSettingsService } from '../../services/clinicSettings';

interface ServiceConfig {
  type: string;
  name: string;
  subtitle: string;
  icon: React.ComponentType<any>;
  color: string;
  bgColor: string;
  borderColor: string;
}

const UnifiedQueueDashboard: React.FC = () => {
  const containerRef = React.useRef<HTMLDivElement>(null);
  const [allQueues, setAllQueues] = useState<QueueDisplayType[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [currentTime, setCurrentTime] = useState(new Date());
  const [lastUpdate, setLastUpdate] = useState<Date | null>(null);
  const [isFullScreen, setIsFullScreen] = useState(false);
  const [hiddenServices, setHiddenServices] = useState<string[]>([]);
  const [showSettings, setShowSettings] = useState(false);
  const [clinicName, setClinicName] = useState('KLINIK CONEC');

  // Configuration for different services
  const serviceConfigs: ServiceConfig[] = [
    {
      type: 'doctor',
      name: 'DOKTER',
      subtitle: 'Pemeriksaan Dokter',
      icon: HeartIcon,
      color: 'text-red-600',
      bgColor: 'bg-red-50',
      borderColor: 'border-red-200'
    },
    {
      type: 'nurse',
      name: 'PERAWAT',
      subtitle: 'Tindakan Keperawatan',
      icon: UserIcon,
      color: 'text-pink-600',
      bgColor: 'bg-pink-50',
      borderColor: 'border-pink-200'
    },
    {
      type: 'midwife',
      name: 'BIDAN',
      subtitle: 'Kebidanan & KIA',
      icon: HeartIcon,
      color: 'text-rose-600',
      bgColor: 'bg-rose-50',
      borderColor: 'border-rose-200'
    },
    {
      type: 'lab',
      name: 'LABORATORIUM',
      subtitle: 'Pemeriksaan Lab',
      icon: BeakerIcon,
      color: 'text-blue-600',
      bgColor: 'bg-blue-50',
      borderColor: 'border-blue-200'
    },
    {
      type: 'radiology',
      name: 'RADIOLOGI',
      subtitle: 'Radiologi & Imaging',
      icon: CameraIcon,
      color: 'text-purple-600',
      bgColor: 'bg-purple-50',
      borderColor: 'border-purple-200'
    },
    {
      type: 'dental',
      name: 'GIGI',
      subtitle: 'Poli Gigi & Mulut',
      icon: UserIcon,
      color: 'text-cyan-600',
      bgColor: 'bg-cyan-50',
      borderColor: 'border-cyan-200'
    },
    {
      type: 'ekg',
      name: 'EKG',
      subtitle: 'Rekam Elektrokardiogram',
      icon: ComputerDesktopIcon,
      color: 'text-indigo-600',
      bgColor: 'bg-indigo-50',
      borderColor: 'border-indigo-200'
    },
    {
      type: 'treadmill',
      name: 'TREADMILL',
      subtitle: 'Uji Beban Jantung',
      icon: ComputerDesktopIcon,
      color: 'text-amber-600',
      bgColor: 'bg-amber-50',
      borderColor: 'border-amber-200'
    },
    {
      type: 'audiometry',
      name: 'AUDIOMETRI',
      subtitle: 'Tes Fungsi Pendengaran',
      icon: MagnifyingGlassIcon,
      color: 'text-teal-600',
      bgColor: 'bg-teal-50',
      borderColor: 'border-teal-200'
    },
    {
      type: 'spirometry',
      name: 'SPIROMETRI',
      subtitle: 'Tes Kapasitas Paru',
      icon: BeakerIcon,
      color: 'text-lime-600',
      bgColor: 'bg-lime-50',
      borderColor: 'border-lime-200'
    },
    {
      type: 'pharmacy',
      name: 'APOTEK',
      subtitle: 'Farmasi & Pengambilan Obat',
      icon: BuildingStorefrontIcon,
      color: 'text-green-600',
      bgColor: 'bg-green-50',
      borderColor: 'border-green-200'
    }
  ];

  // Load settings from localStorage
  useEffect(() => {
    const savedHiddenServices = localStorage.getItem('queue-dashboard-hidden-services');
    if (savedHiddenServices) {
      setHiddenServices(JSON.parse(savedHiddenServices));
    }
  }, []);

  // Save hidden services to localStorage
  useEffect(() => {
    localStorage.setItem('queue-dashboard-hidden-services', JSON.stringify(hiddenServices));
  }, [hiddenServices]);

  // Load clinic name
  useEffect(() => {
    const fetchClinic = async () => {
      try {
        const settings = await clinicSettingsService.getPublicSettings();
        if (settings?.clinic_name) {
          setClinicName(settings.clinic_name);
        }
      } catch (err) {
        // silent fallback
      }
    };
    fetchClinic();
  }, []);

  // Full screen functions
  const toggleFullScreen = () => {
    if (!document.fullscreenElement) {
      const target = containerRef.current || document.documentElement;
      if (target.requestFullscreen) {
        target.requestFullscreen().then(() => {
          setIsFullScreen(true);
        }).catch((err) => {
          console.warn('Error attempting to enable fullscreen:', err);
          setIsFullScreen(true);
        });
      } else {
        setIsFullScreen(true);
      }
    } else {
      if (document.exitFullscreen) {
        document.exitFullscreen().then(() => {
          setIsFullScreen(false);
        }).catch((err) => {
          console.warn('Error attempting to exit fullscreen:', err);
          setIsFullScreen(false);
        });
      } else {
        setIsFullScreen(false);
      }
    }
  };

  // Listen for fullscreen changes
  useEffect(() => {
    const handleFullScreenChange = () => {
      setIsFullScreen(!!document.fullscreenElement);
    };

    document.addEventListener('fullscreenchange', handleFullScreenChange);
    return () => document.removeEventListener('fullscreenchange', handleFullScreenChange);
  }, []);

  // Toggle service visibility
  const toggleServiceVisibility = (serviceType: string) => {
    setHiddenServices(prev => 
      prev.includes(serviceType) 
        ? prev.filter(type => type !== serviceType)
        : [...prev, serviceType]
    );
  };

  // Get visible services
  const visibleServices = serviceConfigs.filter(config => !hiddenServices.includes(config.type));

  const loadAllQueues = async () => {
    try {
      setError(null);
      // Get queue display for all services
      const response = await queueService.getQueueDisplay();
      
      // Ensure we have valid data before setting state
      if (response && response.success && Array.isArray(response.data)) {
        setAllQueues(response.data);
      } else {
        // Set to empty array if response is invalid
        setAllQueues([]);
      }
      
      setLastUpdate(new Date());
    } catch (error: any) {
      console.error('Failed to load unified queue data:', error);
      setError('Gagal memuat data antrian');
      // Set to empty array on error to prevent undefined issues
      setAllQueues([]);
    } finally {
      setLoading(false);
    }
  };

  // Update current time every second
  useEffect(() => {
    const timeInterval = setInterval(() => {
      setCurrentTime(new Date());
    }, 1000);

    return () => clearInterval(timeInterval);
  }, []);

  // Load initial data
  useEffect(() => {
    loadAllQueues();
  }, []);

  // Auto refresh every 10 seconds
  useEffect(() => {
    const interval = setInterval(() => {
      loadAllQueues();
    }, 10000);

    return () => clearInterval(interval);
  }, []);

  const formatTime = (date: Date) => {
    return date.toLocaleTimeString('id-ID', {
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit'
    });
  };

  const formatDate = (date: Date) => {
    return date.toLocaleDateString('id-ID', {
      weekday: 'long',
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  const getQueueForService = (serviceType: string): QueueDisplayType | undefined => {
    return allQueues.find(queue => queue.service_type === serviceType);
  };

  const getTotalWaitingCount = () => {
    if (!allQueues || !Array.isArray(allQueues)) {
      return 0;
    }
    return allQueues.reduce((total, queue) => {
      return total + (queue.waiting_queues?.length || 0);
    }, 0);
  };

  const getTotalActiveCount = () => {
    if (!allQueues || !Array.isArray(allQueues)) {
      return 0;
    }
    return allQueues.filter(queue => queue.current_queue !== null).length;
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-16 w-16 border-b-2 border-blue-600 mx-auto mb-4"></div>
          <p className="text-xl text-gray-600">Memuat data antrian...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <ExclamationTriangleIcon className="h-16 w-16 text-red-500 mx-auto mb-4" />
          <h1 className="text-2xl font-bold text-gray-900 mb-2">Error</h1>
          <p className="text-gray-600 mb-4">{error}</p>
          <button
            onClick={loadAllQueues}
            className="bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 flex items-center mx-auto"
          >
            <ArrowPathIcon className="h-5 w-5 mr-2" />
            Coba Lagi
          </button>
        </div>
      </div>
    );
  }

  return (
    <div
      ref={containerRef}
      className={`min-h-screen bg-gray-50 transition-all duration-300 ${
        isFullScreen
          ? 'fixed inset-0 z-50 h-screen w-screen overflow-y-auto flex flex-col bg-gray-50'
          : ''
      }`}
    >
      {/* Header */}
      <div className={`bg-white shadow-sm border-b ${isFullScreen ? 'flex-shrink-0' : ''}`}>
        <div className={`${isFullScreen ? 'max-w-full px-5 py-2.5' : 'max-w-[1700px] mx-auto px-4 sm:px-6 lg:px-8 py-2.5 sm:py-3'}`}>
          <div className="flex items-center justify-between">
            <div>
              <h1 className={`${isFullScreen ? 'text-xl' : 'text-2xl'} font-bold text-gray-900 tracking-tight leading-tight`}>
                Dashboard Antrian Terpadu
              </h1>
              <p className="text-gray-500 text-xs mt-0.5">{clinicName} - Monitoring Semua Layanan</p>
            </div>
            
            <div className="flex items-center space-x-3">
              {/* Control Buttons */}
              <div className="flex items-center space-x-1.5">
                <button
                  onClick={() => setShowSettings(!showSettings)}
                  className={`p-1.5 rounded-lg transition-colors duration-200 ${
                    showSettings 
                      ? 'bg-blue-100 text-blue-600 border border-blue-300' 
                      : 'bg-gray-100 hover:bg-gray-200 text-gray-600'
                  }`}
                  title="Pengaturan Dashboard"
                >
                  <Cog6ToothIcon className="h-4 w-4" />
                </button>
                
                <button
                  onClick={toggleFullScreen}
                  className="p-1.5 rounded-lg bg-blue-100 hover:bg-blue-200 text-blue-600 transition-colors duration-200"
                  title={isFullScreen ? "Keluar Full Screen" : "Full Screen (Sembunyikan Sidebar)"}
                >
                  {isFullScreen ? (
                    <ArrowsPointingInIcon className="h-4 w-4" />
                  ) : (
                    <ArrowsPointingOutIcon className="h-4 w-4" />
                  )}
                </button>
              </div>
              
              {/* Time Display */}
              <div className="text-right pl-3 border-l border-gray-200">
                <div className={`${isFullScreen ? 'text-lg' : 'text-xl'} font-bold font-mono text-gray-900 leading-none`}>
                  {formatTime(currentTime)}
                </div>
                <div className="text-gray-500 text-[11px] mt-0.5">
                  {formatDate(currentTime)}
                </div>
              </div>
            </div>
          </div>

          {/* Summary Stats - Compact Row */}
          <div className="mt-2.5 grid grid-cols-2 md:grid-cols-5 gap-2 sm:gap-2.5">
            <div className="bg-blue-50/90 border border-blue-200 rounded-lg px-2.5 py-1.5 flex items-center">
              <UserGroupIcon className="h-4 w-4 sm:h-5 sm:w-5 text-blue-600 mr-2 flex-shrink-0" />
              <div className="truncate">
                <div className="text-sm sm:text-base font-bold text-blue-900 leading-none">{getTotalWaitingCount()}</div>
                <div className="text-blue-700 text-[10px] sm:text-[11px] truncate">Total Menunggu</div>
              </div>
            </div>
            
            <div className="bg-green-50/90 border border-green-200 rounded-lg px-2.5 py-1.5 flex items-center">
              <SpeakerWaveIcon className="h-4 w-4 sm:h-5 sm:w-5 text-green-600 mr-2 flex-shrink-0" />
              <div className="truncate">
                <div className="text-sm sm:text-base font-bold text-green-900 leading-none">{getTotalActiveCount()}</div>
                <div className="text-green-700 text-[10px] sm:text-[11px] truncate">Sedang Dilayani</div>
              </div>
            </div>
            
            <div className="bg-indigo-50/90 border border-indigo-200 rounded-lg px-2.5 py-1.5 flex items-center">
              <CubeIcon className="h-4 w-4 sm:h-5 sm:w-5 text-indigo-600 mr-2 flex-shrink-0" />
              <div className="truncate">
                <div className="text-sm sm:text-base font-bold text-indigo-900 leading-none">{serviceConfigs.length}</div>
                <div className="text-indigo-700 text-[10px] sm:text-[11px] truncate">Total Layanan</div>
              </div>
            </div>
            
            <div className="bg-orange-50/90 border border-orange-200 rounded-lg px-2.5 py-1.5 flex items-center">
              <ComputerDesktopIcon className="h-4 w-4 sm:h-5 sm:w-5 text-orange-600 mr-2 flex-shrink-0" />
              <div className="truncate">
                <div className="text-sm sm:text-base font-bold text-orange-900 leading-none">{Array.isArray(allQueues) ? allQueues.filter(q => q.is_active).length : 0}</div>
                <div className="text-orange-700 text-[10px] sm:text-[11px] truncate">Layanan Aktif</div>
              </div>
            </div>
            
            <div className="bg-purple-50/90 border border-purple-200 rounded-lg px-2.5 py-1.5 flex items-center">
              <ClockIcon className="h-4 w-4 sm:h-5 sm:w-5 text-purple-600 mr-2 flex-shrink-0" />
              <div className="truncate">
                <div className="text-xs sm:text-sm font-bold text-purple-900 leading-none">
                  {lastUpdate ? formatTime(lastUpdate) : '-'}
                </div>
                <div className="text-purple-700 text-[10px] sm:text-[11px] truncate">Update Terakhir</div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Settings Panel - Positioned as overlay in fullscreen */}
      {showSettings && (
        <div className={`${isFullScreen ? 'absolute top-0 left-0 right-0 z-50 bg-white border-b shadow-lg' : 'bg-white border-b'}`}>
          <div className={`${isFullScreen ? 'max-w-full' : 'max-w-7xl'} mx-auto px-4 sm:px-6 lg:px-8 py-4`}>
            <div className="flex items-center justify-between mb-4">
              <h3 className={`${isFullScreen ? 'text-base' : 'text-lg'} font-semibold text-gray-900`}>Pengaturan Dashboard</h3>
              <button
                onClick={() => setShowSettings(false)}
                className="p-1 rounded-lg hover:bg-gray-100"
              >
                <XMarkIcon className="h-5 w-5 text-gray-500" />
              </button>
            </div>
            
            <div>
              <h4 className="text-sm font-medium text-gray-700 mb-3">Pilih Layanan yang Ditampilkan:</h4>
              <div className={`grid gap-2 ${isFullScreen ? 'grid-cols-4 sm:grid-cols-6 lg:grid-cols-8' : 'grid-cols-2 sm:grid-cols-3 lg:grid-cols-4'}`}>
                {serviceConfigs.map((config) => {
                  const isHidden = hiddenServices.includes(config.type);
                  const IconComponent = config.icon;
                  
                  return (
                    <button
                      key={config.type}
                      onClick={() => toggleServiceVisibility(config.type)}
                      className={`flex items-center ${isFullScreen ? 'p-2' : 'p-3'} rounded-lg border-2 transition-all duration-200 ${
                        isHidden 
                          ? 'border-gray-200 bg-gray-50 text-gray-400' 
                          : `${config.borderColor} ${config.bgColor} ${config.color}`
                      }`}
                    >
                      <IconComponent className={`${isFullScreen ? 'h-4 w-4 mr-1' : 'h-5 w-5 mr-2'}`} />
                      <span className={`${isFullScreen ? 'text-xs' : 'text-sm'} font-medium`}>{config.name}</span>
                      <div className="ml-auto">
                        {isHidden ? (
                          <EyeSlashIcon className="h-3 w-3" />
                        ) : (
                          <EyeIcon className="h-3 w-3" />
                        )}
                      </div>
                    </button>
                  );
                })}
              </div>
              
              <div className="mt-3 flex items-center justify-between">
                <div className={`${isFullScreen ? 'text-xs' : 'text-sm'} text-gray-600`}>
                  {visibleServices.length} dari {serviceConfigs.length} layanan ditampilkan
                </div>
                <div className="flex space-x-2">
                  <button
                    onClick={() => setHiddenServices([])}
                    className="px-2 py-1 text-xs font-medium text-blue-600 bg-blue-50 border border-blue-200 rounded hover:bg-blue-100"
                  >
                    Tampilkan Semua
                  </button>
                  <button
                    onClick={() => setHiddenServices(serviceConfigs.map(c => c.type))}
                    className="px-2 py-1 text-xs font-medium text-gray-600 bg-gray-50 border border-gray-200 rounded hover:bg-gray-100"
                  >
                    Sembunyikan Semua
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Main Content */}
      <div className={`${isFullScreen ? 'flex-1 overflow-y-auto max-w-full px-5 py-2.5' : 'max-w-[1700px] mx-auto px-4 sm:px-6 lg:px-8 py-2.5 sm:py-3'} w-full ${isFullScreen && showSettings ? 'pt-32' : ''}`}>
        <div className="grid gap-2.5 sm:gap-3 grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 2xl:grid-cols-4">
          {visibleServices.map((config) => {
            const queueData = getQueueForService(config.type);
            const IconComponent = config.icon;
            const waitingCount = queueData?.waiting_queues?.length || 0;
            const hasCurrent = !!queueData?.current_queue;

            return (
              <div
                key={config.type}
                className={`${config.bgColor} ${config.borderColor} border-2 rounded-xl transition-all duration-300 hover:shadow-md flex flex-col justify-between min-h-[168px] sm:min-h-[175px] max-h-[220px] p-3 sm:p-3.5 relative overflow-hidden`}
              >
                {/* Service Header */}
                <div className="flex items-center justify-between pb-2 border-b border-gray-200/60 mb-2">
                  <div className="flex items-center space-x-2.5 min-w-0">
                    <div className={`p-1.5 rounded-lg bg-white shadow-xs ${config.color} flex-shrink-0`}>
                      <IconComponent className="h-4 w-4" />
                    </div>
                    <div className="min-w-0">
                      <h2 className={`text-sm font-bold tracking-tight leading-tight truncate ${config.color}`}>
                        {config.name}
                      </h2>
                      <span className="text-[10px] text-gray-500 font-medium block leading-none mt-0.5 truncate">
                        {config.subtitle || 'Layanan Poli'}
                      </span>
                    </div>
                  </div>
                  <div className={`px-2 py-0.5 rounded-full text-[10px] font-semibold flex items-center space-x-1 shadow-2xs flex-shrink-0 ${
                    hasCurrent
                      ? 'bg-green-100 text-green-700 border border-green-300'
                      : 'bg-white/80 text-gray-600 border border-gray-200'
                  }`}>
                    <span className={`w-1.5 h-1.5 rounded-full ${hasCurrent ? 'bg-green-500 animate-pulse' : 'bg-gray-400'}`}></span>
                    <span>{hasCurrent ? 'Memanggil' : 'Standby'}</span>
                  </div>
                </div>

                {/* Body Content */}
                {hasCurrent && queueData?.current_queue ? (
                  /* Active Called Queue */
                  <div className="flex-1 flex flex-col justify-between space-y-1.5">
                    <div className="bg-white rounded-lg p-2.5 border border-green-200 shadow-xs text-center flex-1 flex flex-col justify-center">
                      <div className="flex items-center justify-center space-x-1 text-green-600 mb-0.5">
                        <SpeakerWaveIcon className="h-3 w-3 animate-bounce" />
                        <span className="text-[10px] font-bold uppercase tracking-wider">Sedang Dipanggil</span>
                      </div>
                      <div className="text-2xl sm:text-3xl font-black font-mono text-gray-900 tracking-wider my-0.5 leading-none">
                        {queueData.current_queue.queue_number}
                      </div>
                      <div className="text-xs font-bold text-gray-800 truncate px-1">
                        {queueData.current_queue.patient_name}
                      </div>
                      <div className="text-[10px] text-gray-400 mt-0.5">
                        Pukul {new Date(queueData.current_queue.called_at).toLocaleTimeString('id-ID', { hour: '2-digit', minute: '2-digit' })}
                      </div>
                    </div>

                    {/* Waiting summary in card */}
                    <div className="bg-white/90 rounded-lg px-2.5 py-1.5 border border-gray-200/80 flex items-center justify-between text-[11px]">
                      <div className="flex items-center space-x-1 text-gray-600">
                        <ClockIcon className="h-3.5 w-3.5 text-blue-500" />
                        <span className="font-medium">Antrian Menunggu:</span>
                      </div>
                      <span className="font-bold text-blue-700 bg-blue-50 px-2 py-0.2 rounded-full border border-blue-200 text-[10px]">
                        {waitingCount} Pasien
                      </span>
                    </div>
                  </div>
                ) : (
                  /* Standby / Waiting for Calls */
                  <div className="flex-1 flex flex-col justify-between">
                    {waitingCount > 0 && queueData?.waiting_queues?.[0] ? (
                      <div className="flex-1 flex flex-col items-center justify-center py-2 px-2 text-center rounded-lg bg-white/85 border border-blue-200 shadow-xs my-0.5">
                        <span className="text-[10px] font-bold text-blue-600 uppercase tracking-wider">Antrian Siap Dipanggil</span>
                        <div className="text-xl sm:text-2xl font-black font-mono text-gray-900 my-0.5 leading-none">
                          {queueData.waiting_queues[0].queue_number}
                        </div>
                        <div className="text-xs font-semibold text-gray-800 truncate max-w-[180px]">
                          {queueData.waiting_queues[0].patient_name}
                        </div>
                        <span className="text-[9px] text-gray-400 mt-0.5">Siap dipanggil petugas</span>
                      </div>
                    ) : (
                      <div className="flex-1 flex flex-col items-center justify-center py-2.5 px-2 text-center rounded-lg bg-white/70 border border-dashed border-gray-300/80 my-0.5">
                        <div className="w-7 h-7 rounded-lg bg-white shadow-xs border border-gray-100 flex items-center justify-center mb-1 text-gray-400">
                          <UserGroupIcon className="h-3.5 w-3.5" />
                        </div>
                        <div className="text-xs font-bold text-gray-700 leading-tight">Belum Ada Panggilan</div>
                        <div className="text-[10px] text-gray-400 mt-0.5">Ruangan siap melayani pasien</div>
                      </div>
                    )}

                    {/* Footer summary in card */}
                    <div className="bg-white/80 rounded-lg px-2.5 py-1.5 border border-gray-200/60 flex items-center justify-between text-[11px] mt-1.5">
                      <div className="flex items-center space-x-1 text-gray-500">
                        <ClockIcon className="h-3.5 w-3.5 text-gray-400" />
                        <span>Antrian Menunggu:</span>
                      </div>
                      <span className={`font-semibold px-2 py-0.5 rounded-full text-[10px] ${
                        waitingCount > 0
                          ? 'bg-blue-100 text-blue-700 font-bold border border-blue-200'
                          : 'bg-gray-100 text-gray-500'
                      }`}>
                        {waitingCount} Pasien
                      </span>
                    </div>
                  </div>
                )}
              </div>
            );
          })}
        </div>

        {/* Additional Info - Compact */}
        {!isFullScreen && (
          <div className="mt-3.5 bg-white rounded-xl shadow-xs border border-gray-200 p-3.5">
            <h3 className="text-sm font-semibold text-gray-900 mb-2">Informasi Dashboard</h3>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-2 text-xs text-gray-600">
              <div className="flex items-center">
                <ArrowPathIcon className="h-3.5 w-3.5 mr-1.5 text-green-500" />
                <span><strong>Auto Refresh:</strong> 10 detik</span>
              </div>
              <div className="flex items-center">
                <CubeIcon className="h-3.5 w-3.5 mr-1.5 text-blue-500" />
                <span><strong>Layanan:</strong> {serviceConfigs.length}</span>
              </div>
              <div className="flex items-center">
                <ComputerDesktopIcon className="h-3.5 w-3.5 mr-1.5 text-orange-500" />
                <span><strong>Aktif:</strong> {Array.isArray(allQueues) ? allQueues.filter(q => q.is_active).length : 0}/{Array.isArray(allQueues) ? allQueues.length : 0}</span>
              </div>
              <div className="flex items-center">
                <ClockIcon className="h-3.5 w-3.5 mr-1.5 text-purple-500" />
                <span><strong>Update:</strong> {lastUpdate ? lastUpdate.toLocaleTimeString('id-ID', {hour: '2-digit', minute: '2-digit'}) : '-'}</span>
              </div>
            </div>
            
            {/* Service List */}
            <div className="mt-2.5 pt-2.5 border-t border-gray-100">
              <div className="text-[11px] text-gray-500 mb-1.5">Layanan Tersedia:</div>
              <div className="flex flex-wrap gap-1.5">
                {serviceConfigs.map((config) => {
                  const isActive = Array.isArray(allQueues) ? allQueues.find(q => q.service_type === config.type)?.is_active : false;
                  return (
                    <span 
                      key={config.type}
                      className={`px-2 py-0.5 rounded-full text-[11px] font-medium ${
                        isActive 
                          ? `${config.color} ${config.bgColor} border ${config.borderColor}`
                          : 'text-gray-400 bg-gray-100 border border-gray-200'
                      }`}
                    >
                      {config.name}
                    </span>
                  );
                })}
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Auto Refresh Indicator */}
      <div className="fixed bottom-3 right-4 z-20 pointer-events-none">
        <div className="bg-white/90 backdrop-blur-xs shadow-md rounded-full px-3 py-1 flex items-center space-x-1.5 border border-gray-200/80">
          <div className="w-1.5 h-1.5 bg-green-500 rounded-full animate-pulse"></div>
          <span className="text-[11px] font-medium text-gray-600">Auto refresh aktif</span>
        </div>
      </div>
    </div>
  );
};

export default UnifiedQueueDashboard;